import { useEffect, useMemo, useRef } from "react";
import { Toast } from "@shopify/polaris";
import stripeLogo from "../../assets/stripe-logo.svg";
import lsLogo from "../../assets/lemonsqueezy-logo.svg";
import { OnboardingWizard } from "../OnboardingWizard";
import { DockAppearanceEditor } from "./tabs/AppearanceTab";
import { AssistantTab } from "./tabs/AssistantTab";
import { AnalyticsTab } from "./tabs/AnalyticsTab";
import { SettingsLayout, type SettingsLayoutVariant } from "./layout/SettingsLayout";
import { SettingsModalStack } from "./layout/SettingsModalStack";
import { SubscriptionTab } from "./tabs/SubscriptionTab";
import { getSettingsTabs, SHOW_PHASE2_TIERS, THEME_COLOR } from "./constants";
import { createSettingsHandlers } from "./logic";
import { PLAN_COMPARISON_SECTIONS, PLAN_DETAILS, formatRetentionLabel } from "./planData";
import { DEFAULT_MASTER_PROMPT, DEFAULT_SETTINGS, type Settings } from "./types";
import { DENSITY_STORAGE_KEY, useSettingsPageState } from "./hooks/useSettingsPageState";
import { useSettingsPageEffects } from "./hooks/useSettingsPageEffects";
import { useSettingsModalHandlers } from "./hooks/useSettingsModalHandlers";
import { useSettingsPageViewModel } from "./hooks/useSettingsPageViewModel";

type SettingsWorkspaceProps = {
  shopDomain: string;
  initialTabId?: string;
  layoutVariant?: SettingsLayoutVariant;
  showOnboarding?: boolean;
};

export function SettingsWorkspace({
  shopDomain,
  initialTabId,
  layoutVariant = "dashboard",
  showOnboarding = true,
}: SettingsWorkspaceProps) {
  const { form, kb, training, billing, analytics, modals, holiday, ui } =
    useSettingsPageState(shopDomain, DEFAULT_SETTINGS);

  const isShopifyStore = !form.formState.platform || form.formState.platform === "shopify";
  const tabs = useMemo(() => getSettingsTabs(form.formState.planTier), [form.formState.planTier]);
  const appliedInitialTabRef = useRef<string | null>(null);

  useEffect(() => {
    if (!initialTabId || appliedInitialTabRef.current === initialTabId) return;
    const index = tabs.findIndex((tab) => tab.id === initialTabId);
    if (index < 0) return;
    appliedInitialTabRef.current = initialTabId;
    if (ui.selectedTab !== index) ui.setSelectedTab(index);
  }, [initialTabId, tabs, ui.selectedTab, ui.setSelectedTab]);

  const {
    closeTrainingModal,
    commitPlanChange,
    doWordPressCheckout,
    fetchAnalytics,
    fetchConfig,
    fetchGaps,
    fetchKb,
    fetchQueryLog,
    handleChange,
    handleDeleteKbItem,
    handleDiscard,
    handleExportCSV,
    handleExportJSON,
    handleImportFile,
    handleSeedGreetings,
    handleMonthChange,
    handleOnboardingComplete,
    handleOnboardingDismiss,
    handleSave,
    handleWipeAnalytics,
    handleWipeKb,
    normalizeScrapeUrl,
    openNewTopicModal,
    showToast,
    toggleToast,
  } = createSettingsHandlers({
    ...form, ...kb, ...training, ...billing, ...analytics, ...modals, ...holiday, ...ui,
    DEFAULT_SETTINGS,
    isShopifyStore,
    shopDomain,
    tabs,
  });

  useSettingsPageEffects({
    analytics,
    form,
    kb,
    ui,
    shopDomain,
    DENSITY_STORAGE_KEY,
    fetchAnalytics,
    fetchConfig,
    fetchGaps,
    fetchKb,
    fetchQueryLog,
    normalizeScrapeUrl,
    showToast,
  });

  const {
    activeTabId,
    analyticsActions,
    assistantActions,
    currentTier,
    dirtyMessage,
    filteredKbItems,
    isAgentPlus,
    isFree,
    isGrowthPlus,
    isRepPlus,
    paginatedGapItems,
    paginatedKbItems,
    safeSelectedTab,
    subscriptionActions,
    tabHeroMeta,
    visiblePlanIds,
  } = useSettingsPageViewModel({
    form,
    kb,
    training,
    billing,
    analytics,
    modals,
    ui,
    showPhase2Tiers: SHOW_PHASE2_TIERS,
    tabs,
    shopDomain,
    fetchAnalytics,
    fetchKb,
    fetchQueryLog,
    openNewTopicModal,
    setLoading: form.setLoading,
    showToast,
  });

  const {
    handleDeleteTopicConfirm,
    handleEditKbItem,
    handleHolidayAddOrUpdate,
    handleHolidaySave,
    handleOpenDuplicateTopic,
    handleProviderContinue,
    handleTrainingSave,
    handleViewKbItem,
    resetHolidayModal,
    resetProviderModal,
  } = useSettingsModalHandlers({
    form,
    kb,
    training,
    billing,
    modals,
    holiday,
    closeTrainingModal,
    doWordPressCheckout,
    fetchKb,
    handleChange,
    shopDomain,
    showToast,
  });

  const toastMarkup = ui.toastActive ? (
    <Toast content={ui.toastMsg} onDismiss={toggleToast} />
  ) : null;

  return (
    <>
      <SettingsLayout
        activeTabId={activeTabId}
        density={form.density}
        dirtyMessage={dirtyMessage}
        hero={tabHeroMeta[activeTabId]}
        isDirty={form.isDirty}
        loading={form.loading}
        onDiscard={handleDiscard}
        onSave={handleSave}
        safeSelectedTab={safeSelectedTab}
        setSelectedTab={ui.setSelectedTab}
        tabs={tabs}
        variant={layoutVariant}
        visualEditor={
          <DockAppearanceEditor
            config={form.formState as any}
            onChange={(key, value) => handleChange(key as keyof Settings, value)}
            onSave={handleSave}
            shopDomain={shopDomain}
            isGrowthPlus={isGrowthPlus}
            isAgentPlus={isAgentPlus}
            isDirty={form.isDirty}
            setLocalHolidays={holiday.setLocalHolidays}
            setShowHolidayModal={modals.setShowHolidayModal}
          />
        }
      >
        {tabs[safeSelectedTab].id === "assistant" && (
          <AssistantTab
            analyticsLoading={analytics.analyticsLoading}
            filteredKbItems={filteredKbItems}
            gapItemsPerPage={kb.gapItemsPerPage}
            gapPage={kb.gapPage}
            handleDeleteKbItem={handleDeleteKbItem}
            handleExportCSV={() => handleExportCSV(filteredKbItems)}
            handleExportJSON={() => handleExportJSON(filteredKbItems)}
            handleImportFile={handleImportFile}
            onSeedGreetings={handleSeedGreetings}
            isAgentPlus={isAgentPlus}
            isRefreshing={analytics.isRefreshing}
            isRepPlus={isRepPlus}
            kbGaps={kb.kbGaps}
            kbItemsPerPage={kb.kbItemsPerPage}
            kbPage={kb.kbPage}
            kbSearch={kb.kbSearch}
            loading={form.loading}
            onCopyMasterPrompt={() => assistantActions.onCopyMasterPrompt(DEFAULT_MASTER_PROMPT)}
            onDeleteKbItem={handleDeleteKbItem}
            onEditKbItem={handleEditKbItem}
            onViewKbItem={handleViewKbItem}
            onOpenNewTopicModal={assistantActions.onOpenNewTopicModal}
            onOpenPlansTab={assistantActions.onOpenPlansTab}
            onScanPage={assistantActions.onScanPage}
            onShowWipeModal={assistantActions.onShowWipeModal}
            onTrainGap={assistantActions.onTrainGap}
            paginatedGapItems={paginatedGapItems}
            paginatedKbItems={paginatedKbItems}
            scrapeUrl={kb.scrapeUrl}
            setGapPage={kb.setGapPage}
            setKbPage={kb.setKbPage}
            setKbSearch={kb.setKbSearch}
            setScrapeUrl={kb.setScrapeUrl}
            setShowSetupGuide={kb.setShowSetupGuide}
            showPhase2Tiers={SHOW_PHASE2_TIERS}
            showSetupGuide={kb.showSetupGuide}
            themeColor={THEME_COLOR}
          />
        )}

        {tabs[safeSelectedTab].id === "analytics" && (
          <AnalyticsTab
            analyticsData={analytics.analyticsData}
            analyticsLoading={analytics.analyticsLoading}
            analyticsPeriod={analytics.analyticsPeriod}
            chatbotSectionOpen={analytics.chatbotSectionOpen}
            isAgentPlus={isAgentPlus}
            isFree={isFree}
            isRefreshing={analytics.isRefreshing}
            isRepPlus={isRepPlus}
            kbGaps={kb.kbGaps}
            onOpenAppearanceTab={analyticsActions.onOpenAppearanceTab}
            onOpenPlansTab={analyticsActions.onOpenPlansTab}
            onRefresh={analyticsActions.onRefresh}
            onShowAnalyticsWipeModal={analyticsActions.onShowAnalyticsWipeModal}
            queryLog={analytics.queryLog}
            queryLogLoading={analytics.queryLogLoading}
            setAnalyticsPeriod={analytics.setAnalyticsPeriod}
            setChatbotSectionOpen={analytics.setChatbotSectionOpen}
            setVisitorSectionOpen={analytics.setVisitorSectionOpen}
            themeColor={THEME_COLOR}
            visitorSectionOpen={analytics.visitorSectionOpen}
          />
        )}

        {tabs[safeSelectedTab].id === "plans" && (
          <SubscriptionTab
            billingCycle={billing.billingCycle}
            billingInfo={billing.billingInfo}
            currentTier={currentTier}
            expandedFaq={billing.expandedFaq}
            formatRetentionLabel={formatRetentionLabel}
            isShopifyStore={isShopifyStore}
            onOpenPlanModal={subscriptionActions.onOpenPlanModal}
            onOpenProviderModal={subscriptionActions.onOpenProviderModal}
            planComparisonSections={PLAN_COMPARISON_SECTIONS}
            planDetails={PLAN_DETAILS}
            setBillingCycle={billing.setBillingCycle}
            setExpandedFaq={billing.setExpandedFaq}
            visiblePlanIds={visiblePlanIds}
          />
        )}
      </SettingsLayout>

      <SettingsModalStack
        kb={kb}
        training={training}
        billing={billing}
        modals={modals}
        ui={ui}
        holiday={holiday}
        analyticsWipeLoading={analytics.analyticsWipeLoading}
        wipeConfirmText={analytics.wipeConfirmText}
        setWipeConfirmText={analytics.setWipeConfirmText}
        loading={form.loading}
        filteredKbItems={filteredKbItems}
        currentTier={currentTier}
        shopDomain={shopDomain}
        showPhase2Tiers={SHOW_PHASE2_TIERS}
        stripeLogo={stripeLogo}
        lemonSqueezyLogo={lsLogo}
        closeTrainingModal={closeTrainingModal}
        onCommitPlanChange={commitPlanChange}
        handleDeleteTopicConfirm={handleDeleteTopicConfirm}
        handleExportCSV={handleExportCSV}
        handleExportJSON={handleExportJSON}
        handleHolidayAddOrUpdate={handleHolidayAddOrUpdate}
        handleHolidaySave={handleHolidaySave}
        handleMonthChange={handleMonthChange}
        handleOpenDuplicateTopic={handleOpenDuplicateTopic}
        handleProviderContinue={handleProviderContinue}
        handleTrainingSave={handleTrainingSave}
        handleWipeAnalytics={handleWipeAnalytics}
        handleWipeKb={handleWipeKb}
        resetHolidayModal={resetHolidayModal}
        resetProviderModal={resetProviderModal}
      />

      {toastMarkup}

      {showOnboarding && ui.showOnboarding && form.configLoaded && (
        <OnboardingWizard
          planTier={form.formState.planTier}
          shopDomain={shopDomain}
          onComplete={handleOnboardingComplete}
          onDismiss={handleOnboardingDismiss}
        />
      )}
    </>
  );
}
